import type { ReactNode } from "react";
import { observer } from "mobx-react";

import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned cluster: buttons, switchers, search inputs. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The canonical page-level title row: heading + optional description on the
 * left, an actions cluster on the right, stacking on small screens. Pages were
 * hand-rolling this shape with drifting type sizes (text-lg/xl/2xl) — compose
 * this instead so every surface reads the same. Section/card/dialog headers
 * are NOT this component; those keep their local chrome.
 *
 * Layout-only by design (no own padding) so it can sit at the top of a route or
 * be embedded inside a Panel/settings shell. Top-level routes apply the standard
 * page chrome around it: `px-6 pt-6` (24px) padding and, when there are multiple
 * views, a `<PageTabs className="mt-4" />` (16px) row directly below.
 *
 * Header action sizing convention (per the common-header spec): the primary
 * action is `<Button size="lg">` (32px tall) as the right-most control;
 * secondary actions are `<Button variant="outline" size="icon-lg">` (32x32)
 * to its left, separated by the cluster's 8px gap.
 */
export const PageHeader = observer(function PageHeader({
  title,
  description,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn("flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", className)}
    >
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
});
